import { useCallback, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../../api/fastapi";
import { useHookCache } from "../../lib/hookCache";
import type {
  AuditSession,
  ProcedureSessionDocumentsResponse,
} from "../../models/audit/auditSession";

/**
 * The write half of the per-document analytics-input flag.
 *
 * The flag's grain is one state per document per test table, so the request
 * carries a session and a document and nothing else. There is deliberately no
 * sample in it: a file attached to several samples is an analytics input for all
 * of them or none, and a per-sample body would be the first step towards a
 * divergence the data model cannot hold.
 *
 * The route sits beside the other per-document writes on the match session,
 * which is the surface that owns the document-to-session row the flag lives on.
 */
const analyticsInputPath = (sessionId: string, documentId: string): string =>
  `/matching/match-session/${sessionId}/document/${documentId}/update-analytics-input`;

export interface AnalyticsInputFlagActions {
  /**
   * True while any flag write on this session is in flight.
   *
   * Session-wide, so a caller that gates a control on it greys out every row.
   * Prefer `isPendingFor`, which answers for the row the auditor acted on.
   */
  pending: boolean;
  /** True while this document's own flag write is in flight. */
  isPendingFor: (documentId: string) => boolean;
  /**
   * Write the flag, answering whether it landed.
   *
   * It never rejects: the failure is toasted here, so a caller whose whole
   * gesture is this one write can ignore the answer. A caller that has already
   * committed something else on the strength of this write cannot — for it,
   * "resolved" and "succeeded" are not the same thing.
   */
  setAnalyticsInput: (documentId: string, next: boolean) => Promise<boolean>;
}

export function useAnalyticsInputFlag(
  procedureSessionId: string | undefined,
): AnalyticsInputFlagActions {
  const { setEntry, invalidate } = useHookCache();
  // The in-flight documents rather than a single boolean: overlapping writes
  // each own their own entry, so the first to settle cannot re-enable a row
  // whose write is still open.
  const [inFlight, setInFlight] = useState<Set<string>>(new Set());

  const setAnalyticsInput = useCallback(
    async (documentId: string, next: boolean): Promise<boolean> => {
      if (!procedureSessionId) return false;
      const sessionKey = ["procedure-session", procedureSessionId];
      const documentsKey = [...sessionKey, "documents"];

      // Both caches carry the same document rows, and the indicator is rendered
      // from each: updating one alone shows the file as flagged on the Documents
      // tab and unflagged on the Support tab, which is the state the indicator
      // exists to rule out.
      //
      // A row may arrive with no flag on it at all, which is why the constraint
      // admits one; the row this write touches leaves with a definite answer
      // either way, because the value written is the one being sent.
      const applyFlag = <
        T extends {
          isAnalyticsInput?: boolean;
          id: string;
          documentId: string;
        },
      >(
        documents: T[],
      ): T[] =>
        documents.map((document) =>
          document.documentId === documentId || document.id === documentId
            ? {
                ...document,
                isAnalyticsInput: next,
              }
            : document,
        );

      setEntry<AuditSession>(sessionKey, (prev) => ({
        ...prev,
        documents: applyFlag(prev.documents),
      }));
      setEntry<ProcedureSessionDocumentsResponse>(documentsKey, (prev) => ({
        ...prev,
        documents: applyFlag(prev.documents),
      }));

      setInFlight((prev) => new Set(prev).add(documentId));
      try {
        await fetchFastApi({
          method: "POST",
          path: analyticsInputPath(procedureSessionId, documentId),
          body: { is_analytics_input: next },
          // A caller reads this hook's answer after it has already destroyed
          // state it cannot rebuild, so an error envelope must not resolve:
          // a refusal that arrives as a value is indistinguishable here from
          // the write having landed.
          throwOnErrorEnvelope: true,
        });
        await invalidate(sessionKey);
        return true;
      } catch (error) {
        console.error("Failed to update the analytics-input flag", error);
        toast.error("Failed to update the analytics-input flag");
        // Re-read rather than restore a snapshot: the optimistic write above
        // touched two entries, and a refetch is the only thing that cannot
        // leave them disagreeing.
        await invalidate(sessionKey);
        return false;
      } finally {
        setInFlight((prev) => {
          const remaining = new Set(prev);
          remaining.delete(documentId);
          return remaining;
        });
      }
    },
    [procedureSessionId, setEntry, invalidate],
  );

  const isPendingFor = useCallback(
    (documentId: string) => inFlight.has(documentId),
    [inFlight],
  );

  return { pending: inFlight.size > 0, isPendingFor, setAnalyticsInput };
}
